import {
    AttributeSlot,
    PerspectiveAttribute,
    SitePerspectivePhase,
    SitePerspectiveType,
    attributeSlotsFor,
    sitePerspectiveAttributes,
} from '@the-real-insight/tri-model';

import { ProcessTypeKey } from './processModel';
import { projectTypeOf } from './documentSlots';

/**
 * Welche Attribute eine Perspektive zeigt.
 *
 * Zwei Quellen, eine Liste. `sitePerspectiveAttributes` führt, was eine Perspektive unabhängig
 * davon fragt, wo im Verlauf sie steht — Grundstücksgröße, Eigentumsverhältnisse, Bodenrichtwert.
 * Die Prozessdefinitionen führen, was ein *Tupel* aus Prozesstyp, Phase und Perspektive fragt: die
 * Zeilen der Datenanforderungslisten, die kein Dokument verlangen, sondern einen Wert.
 *
 * Beides landet auf demselben Datensatz, weil `SitePerspective` über genau dieses Tupel
 * geschlüsselt ist. Die Phase gehört zu seiner Identität; ein phasenabhängiges Attribut hat dort
 * längst eine Zeile.
 *
 * Die Reihenfolge ist nicht beliebig: Erst die Attribute des Typs, dann die der Phase. Die des Typs
 * stehen seit jeher oben, und eine Kachel zeigt nur die ersten Zeilen — wer die Reihenfolge
 * umdrehte, nähme dem Leser die Werte weg, die er dort gewohnt ist.
 */
export const perspectiveAttributes = (
    typeKey: ProcessTypeKey,
    phase: string | null | undefined,
    perspectiveType: SitePerspectiveType,
): PerspectiveAttribute[] => {
    const eigene = sitePerspectiveAttributes[perspectiveType] ?? [];
    const projectType = projectTypeOf(typeKey);

    if (!projectType || !phase) return eigene;

    const derPhase = attributeSlotsFor(projectType, phase as SitePerspectivePhase, perspectiveType);

    // Ein Name kann in beiden stehen. Heute tut er es nicht — die 118 Attribute der Vorlagen
    // treffen keinen der 69 vorhandenen —, aber das ist ein Befund über den heutigen Stand und
    // keine Eigenschaft der Struktur. Doppelt gezeigt wäre dasselbe Feld zweimal auf dem Schirm.
    const bekannt = new Set(eigene.map((attribute) => attribute.name));

    return [...eigene, ...derPhase.filter((slot) => !bekannt.has(slot.name))];
};

/**
 * Die Werteliste eines Attributs — leer für alles, was keine Auswahl ist.
 *
 * Immer eine Liste, nie `undefined`: Der Aufrufer fragt nach ihrer Länge, und eine Prüfung auf
 * „vorhanden" vor jeder solchen Frage wäre Rauschen um eine Unterscheidung, die ihn nichts angeht.
 */
export const optionsOf = (attribute: PerspectiveAttribute): NonNullable<AttributeSlot['options']> =>
    (attribute as AttributeSlot).options ?? [];
